import useInfiniteScrollTable from 'common/components/BasicTable/useInfiniteScrollTable'
import PageWrapper from 'common/components/PageWrapper'
import { fNumber } from 'common/utils/formatNumber'
import { useStockHistoryStatisticV3, useStockHistoryV3 } from 'modules/inventory/api/queries'
import { StockHistoryStatisticV3, StockHistoryV3 } from 'modules/inventory/api/type'
import ActionPill from 'modules/inventory/components/ActionPill'
import FilterDialog, { DEFAULT_FILTER, FilterState, filterToApiBase } from 'modules/inventory/components/StockHistoryFilterDialog'
import StockHistoryDetailDialog from 'modules/inventory/pages/stock/StockHistoryDetailDialog'
import { formatThaiShortBEWithTime } from 'modules/inventory/helper/format_date'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import {
  Avatar,
  Button,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'

import NoDataNoBorder from 'components/NoDataNoBorder'

const TABS: { key: string | undefined; labelKey: string; statKey: keyof StockHistoryStatisticV3 | 'total_history' }[] = [
  { key: undefined, labelKey: 'stock.history.tab.all', statKey: 'total_history' },
  { key: 'use_product', labelKey: 'stock.history.tab.use_product', statKey: 'use_product' },
  { key: 'add_stock', labelKey: 'stock.history.tab.add_stock', statKey: 'add_stock' },
  { key: 'adjust_quantity', labelKey: 'stock.history.tab.adjust_quantity', statKey: 'adjust_quantity' },
  { key: 'transfer_warehouse', labelKey: 'stock.history.tab.transfer_warehouse', statKey: 'transfer_warehouse' },
  { key: 'edit_stock', labelKey: 'stock.history.tab.edit_stock', statKey: 'edit_stock' },
  { key: 'delete_stock', labelKey: 'stock.history.tab.delete_stock', statKey: 'delete_stock' },
]

// ---------- Main Page ----------

const StockPage = () => {
  const { t } = useTranslation('stock')
  const theme = useTheme()
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const location = useLocation()
  const productId: string | undefined = (location.state as any)?.productId
  const fromProduct = Boolean(productId)

  const handleBack = () => {
    if (fromProduct) navigate(-1)
    else navigate('/dashboard/product')
  }

  const [transactionType, setTransactionType] = useState<string | undefined>(undefined)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER)
  const [detailItem, setDetailItem] = useState<StockHistoryV3 | null>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchText.trim()), 800)
    return () => clearTimeout(id)
  }, [searchText])

  const apiBase = filterToApiBase(filter)
  const apiBaseWithSearch = {
    ...apiBase,
    ...(productId ? { productId } : debouncedSearch ? { productName: debouncedSearch } : {}),
  }
  const queryParams = {
    ...apiBaseWithSearch,
    ...(transactionType ? { transactionType } : {}),
  }

  const { data: statData } = useStockHistoryStatisticV3(apiBaseWithSearch)
  const { data, isLoading, hasNextPage, fetchNextPage } = useStockHistoryV3(queryParams)
  const items = data?.pages?.flatMap((p) => p.items || []).filter(Boolean) ?? []

  const handleScroll = useInfiniteScrollTable({
    isLoading,
    onLoading: () => { if (hasNextPage) fetchNextPage() },
  })

  React.useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 })
  }, [transactionType, filter])

  const LINKS = [
    { name: t('inventory.title', { defaultValue: 'คลัง' }), href: '/dashboard/product' },
    { name: t('history.stock') },
  ]

  return (
    <PageWrapper
      title={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton size="small" onClick={handleBack}>
            <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="h4">{t('history.stock')}</Typography>
        </Stack>
      }
      titleHead={t('history.stock')}
      links={LINKS}
      action={
        <Button
          variant="outlined"
          color='inherit'
          sx={{ backgroundColor: 'white' }}
          startIcon={<FilterListIcon />}
          onClick={() => setFilterOpen(true)}
        >
          {t('stock.history.filter')}
        </Button>
      }
    >
      {/* Transaction type tabs */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 2,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          backgroundColor: 'white',
          p: 1,
          borderRadius: '20px',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
          alignItems: 'center',
          width: '100%',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {TABS.map((tab) => {
          const count = statData ? (statData[tab.statKey as keyof StockHistoryStatisticV3] as number ?? 0) : 0
          const isActive = transactionType === tab.key
          return (
            <Chip
              key={tab.labelKey}
              label={`${t(tab.labelKey)} (${count})`}
              onClick={() => setTransactionType(tab.key)}
              sx={{
                borderRadius: 1, 
                fontWeight: 500,
                px: 1, 
                height: 36,
                bgcolor: isActive ? 'primary.main' : 'transparent',
                color: isActive ? 'common.white' : '#1C2434',
                border: 'none', 
                fontSize: '14px',
                '&:hover': {
                  bgcolor: isActive ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
                },
                '& .MuiChip-label': {
                  p: '0 8px',
                }
              }}
            />
          )
        })}
      </Stack>

      <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh', bgcolor: 'common.white', borderRadius: '24px', p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('stock.history.list')}
          </Typography>
          {!fromProduct && (
            <TextField
              size="small"
              placeholder={t('stock.history.search')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ width: 240 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small"><SearchIcon fontSize="small" /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Stack>

        {isLoading ? (
          <NoDataNoBorder />
        ) : items.length > 0 ? (
          <TableContainer
            ref={containerRef}
            component={Paper}
            onScroll={handleScroll}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table
              stickyHeader
              sx={{
                '& thead th': {
                  fontSize: '14px',
                  color: theme.palette.common.black,
                  backgroundColor: '#F0F0F0',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& tbody td': { borderBottom: '1px solid', borderColor: 'divider' },
                '& thead th:first-of-type': { borderTopLeftRadius: 12 },
                '& thead th:last-of-type': { borderTopRightRadius: 12 },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('stock.history.col.time')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('stock.product.label')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('stock.history.status')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('dialog.stock.lot')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('stock.warehouse')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{t('stock.history.quantity')}</TableCell>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{t('stock.remain')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('stock.history.remark')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row: StockHistoryV3, idx: number) => (
                  <TableRow key={row.id || idx} hover onClick={() => setDetailItem(row)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatThaiShortBEWithTime(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={row.product_img_url || undefined}
                          alt={row.product_name}
                          variant="rounded"
                          sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'primary.lighter', color: 'primary.main', fontSize: 14, fontWeight: 700 }}
                        >
                          {row.product_name?.[0]?.toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">{row.product_name || '-'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <ActionPill action={row.transaction_type} />
                    </TableCell>
                    <TableCell>{row.stock_lot_number || '-'}</TableCell>
                    <TableCell>{row.warehouse_name || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.transaction_type === 'edit_stock' ? (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: row.used_qty < 0 ? 'error.main' : 'text.primary' }}
                        >
                          {row.used_qty > 0 ? `+${fNumber(row.used_qty)}` : fNumber(row.used_qty)} {row.unit_abbreviation}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: row.remaining_qty < 0 ? 'error.main' : 'text.primary' }}
                      >
                        {fNumber(row.remaining_qty)} {row.unit_abbreviation}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{row.remark || '-'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <NoDataNoBorder />
        )}
      </Card>

      <FilterDialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={filter}
        onConfirm={setFilter}
      />

      <StockHistoryDetailDialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        item={detailItem}
      />
    </PageWrapper>
  )
}

export default StockPage
